import React, { useMemo } from 'react';
import { clsx } from 'clsx';

import { ProgrammeCalendarItem } from '@/types';

type ProgrammeCalendarProps = {
  items: ProgrammeCalendarItem[];
  rangeStart: string;
  rangeEnd: string;
  loading?: boolean;
};

type CalendarEvent = {
  key: string;
  date: string;
  start: string;
  end: string;
  title: string;
  coach: string;
  status: string;
  enrolled: number;
  max: number;
};

const STATUS_CLASS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  full: 'bg-orange-100 text-orange-700 border-orange-200',
  in_progress: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const toDate = (value: string): Date => new Date(`${value}T00:00:00`);
const toDateKey = (value: Date): string => value.toISOString().slice(0, 10);

const buildDateRange = (start: string, end: string): string[] => {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate > endDate) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    dates.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const formatDayLabel = (dateKey: string): string => {
  const date = toDate(dateKey);
  return new Intl.DateTimeFormat('en-NZ', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const formatTime = (value: string | null): string => {
  if (!value) return '--:--';
  return String(value).slice(0, 5);
};

const ProgrammeCalendar: React.FC<ProgrammeCalendarProps> = ({ items, rangeStart, rangeEnd, loading = false }) => {
  const days = useMemo(() => buildDateRange(rangeStart, rangeEnd), [rangeEnd, rangeStart]);

  const eventMap = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};

    items.forEach((item) => {
      const programme = item.programme;
      item.sessions.forEach((session) => {
        const date = String(session.session_date || '').slice(0, 10);
        if (!date) return;

        const event: CalendarEvent = {
          key: `${programme.id}-${session.id}`,
          date,
          start: formatTime(session.start_time),
          end: formatTime(session.end_time),
          title: programme.title,
          coach: programme.coach_name || 'TBD',
          status: programme.status,
          enrolled: Number(programme.enrolled_count || 0),
          max: Number(programme.max_participants || 0),
        };

        if (!map[date]) map[date] = [];
        map[date].push(event);
      });
    });

    Object.keys(map).forEach((date) => {
      map[date].sort((a, b) => a.start.localeCompare(b.start));
    });

    return map;
  }, [items]);

  if (loading) {
    return <div className="text-sm text-gray-500 py-8 text-center">Loading programme calendar...</div>;
  }

  if (days.length === 0) {
    return <div className="text-sm text-gray-500 py-8 text-center">Invalid calendar range.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <div className="grid gap-3 min-w-[980px]" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
        {days.map((day) => {
          const dayEvents = eventMap[day] || [];
          return (
            <div key={day} className="rounded-xl border border-gray-200 bg-gray-50/60">
              <div className="px-3 py-2 border-b border-gray-200 bg-white rounded-t-xl">
                <p className="text-xs font-semibold text-gray-800 uppercase tracking-wide">{formatDayLabel(day)}</p>
                <p className="text-[11px] text-gray-500">{day}</p>
              </div>
              <div className="p-2 space-y-2 min-h-[260px]">
                {dayEvents.length === 0 ? (
                  <div className="text-xs text-gray-400 p-2">No sessions</div>
                ) : dayEvents.map((event) => (
                  <div key={event.key} className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-900 leading-4 break-words">{event.title}</p>
                      <span className={clsx('text-[10px] px-1.5 py-0.5 rounded-full border', STATUS_CLASS[event.status] || STATUS_CLASS.draft)}>
                        {event.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-600">{event.start} - {event.end}</p>
                    <p className="text-[11px] text-gray-500">Coach: {event.coach}</p>
                    <p className="text-[11px] text-gray-500">Capacity: {event.enrolled}/{event.max}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgrammeCalendar;
